// src/design-system/DonutChart.tsx
import type { Tone } from "./tone";

export type DonutSegment = { label: string; value: number; tone: Tone };

/**
 * دونات SVG بنفس هندسة بقية الشارتات: قطاعات ملوّنة بالـ tone الموحّد (data-tone → var(--fg))،
 * والإجمالي مضيء في المنتصف، ومعه legend يوضّح كل تسمية وقيمتها ونسبتها.
 */
export function DonutChart({
  segments, size = 130, thickness = 16, centerLabel,
}: { segments: DonutSegment[]; size?: number; thickness?: number; centerLabel?: string }) {
  const total = segments.reduce((a, s) => a + s.value, 0);
  const r = (size - thickness) / 2;
  const c = size / 2;
  const circumference = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div className="flex flex-wrap items-center gap-5" role="img" aria-label={centerLabel ?? "توزيع"}>
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
          <circle cx={c} cy={c} r={r} fill="none" stroke="rgba(148,180,214,.14)" strokeWidth={thickness} />
          {total > 0 && segments.map((s, i) => {
            if (s.value <= 0) return null;
            const len = (s.value / total) * circumference;
            const dash = `${len} ${circumference - len}`;
            const el = (
              <circle key={i} data-tone={s.tone} cx={c} cy={c} r={r} fill="none" stroke="var(--fg)" strokeWidth={thickness}
                strokeDasharray={dash} strokeDashoffset={-offset} strokeLinecap="butt"
                style={{ filter: "drop-shadow(0 0 6px var(--fg))" }} />
            );
            offset += len;
            return el;
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
          <strong className="mono glow-text text-[22px] font-semibold leading-none" style={{ color: "#F5E4C0", ["--glow" as string]: "rgba(214,172,85,.55)" }}>{total}</strong>
          {centerLabel && <span className="text-[10.5px] text-muted-2">{centerLabel}</span>}
        </div>
      </div>
      <ul className="m-0 flex min-w-[140px] flex-1 list-none flex-col gap-1.5 p-0">
        {segments.map((s, i) => (
          <li key={i} data-tone={s.tone} className="flex items-center gap-2 text-[12.5px]">
            <span className="dot" style={{ width: 7, height: 7 }} />
            <span className="flex-1 text-ink-2">{s.label}</span>
            <strong className="mono" style={{ color: "var(--fg)" }}>{s.value}</strong>
            {total > 0 && <span className="text-[11px] text-muted-2">{Math.round((s.value / total) * 100)}%</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
